import type { ListBuilderConfiguration } from '../types';

/**
 * Shared category column definition
 */
const getCategoryColumn = () =>
	({
		id: 'category',
		fields: [
			{
				id: 'category-name-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'category' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'static',
						staticColor: { text: '#3b82f6' },
					},
				},
				displayStyle: 'text',
			},
			{
				id: 'line-break-1',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'item-count-field',
				dataConfig: {
					type: 'template',
					template: '{itemCount} items',
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h1', text: 'Category' }],
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'fixed', preset: 'medium' },
		verticalAlignment: 'middle',
	}) as const;

/**
 * Shared chart column definition
 */
const getChartColumn = (idPrefix: string) =>
	({
		id: idPrefix === '' ? 'chart' : `chart${idPrefix}`,
		fields: [
			{
				id: `chart${idPrefix === '' ? '' : `-${idPrefix.toLowerCase()}`}-field`,
				dataConfig: {
					type: 'field',
					field: { fieldName: 'monthlyData' },
					defaultValue: '[]',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'custom',
				customComponent: { id: 'client-item-history-trend-chart' },
			},
		],
		headers: [
			{
				id: idPrefix === '' ? 'h4' : `h-chart-${idPrefix.toLowerCase()}`,
				text: '12 Month Trend',
			},
		],
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'middle',
	}) as const;

/**
 * 30/60/90-day quantity columns for the Previously Purchased view, mirroring the
 * item Client History windows: 30-day shows total + order count; 60/90-day show
 * total + max/avg monthly velocity (derived from the trailing monthly buckets).
 */
const getDayWindowColumns = () => [
	{
		id: 'cat-quantities30',
		fields: [
			{
				id: 'cat-total-quantity-30-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'quantityLast30Days' },
					defaultValue: '0',
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					formatOptions: { decimalPlaces: 0 },
				},
				displayStyle: 'number',
			},
			{
				id: 'cat-line-break-30-1',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'cat-purchases-30-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'purchasesLast30Days' },
					defaultValue: '0',
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
					formatOptions: { decimalPlaces: 0 },
				},
				displayStyle: 'number',
			},
		],
		headers: [
			{ id: 'h-cat-30-1', text: '30-Day Total' },
			{ id: 'h-cat-30-lb', text: '' },
			{ id: 'h-cat-30-2', text: '30-Day Orders' },
		],
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'fixed', preset: 'xsmall' },
		verticalAlignment: 'middle',
	} as const,
	{
		id: 'cat-quantities60',
		fields: [
			{
				id: 'cat-total-quantity-60-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'quantityLast60Days' },
					defaultValue: '0',
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					formatOptions: { decimalPlaces: 0 },
				},
				displayStyle: 'number',
			},
			{
				id: 'cat-line-break-60-1',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'cat-max-quantity-60-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'maxQuantityLast60Days' },
					defaultValue: '0',
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
					formatOptions: { decimalPlaces: 0 },
				},
				displayStyle: 'number',
			},
			{
				id: 'cat-line-break-60-2',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'cat-avg-quantity-60-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'avgQuantityLast60Days' },
					defaultValue: '0',
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
					formatOptions: { decimalPlaces: 1 },
				},
				displayStyle: 'number',
			},
		],
		headers: [
			{ id: 'h-cat-60-1', text: '60-Day Total' },
			{ id: 'h-cat-60-lb1', text: '' },
			{ id: 'h-cat-60-2', text: '60-Day Max' },
			{ id: 'h-cat-60-lb2', text: '' },
			{ id: 'h-cat-60-3', text: '60-Day Avg' },
		],
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'fixed', preset: 'xsmall' },
		verticalAlignment: 'middle',
	} as const,
	{
		id: 'cat-quantities90',
		fields: [
			{
				id: 'cat-total-quantity-90-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'quantityLast90Days' },
					defaultValue: '0',
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					formatOptions: { decimalPlaces: 0 },
				},
				displayStyle: 'number',
			},
			{
				id: 'cat-line-break-90-1',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'cat-max-quantity-90-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'maxQuantityLast90Days' },
					defaultValue: '0',
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
					formatOptions: { decimalPlaces: 0 },
				},
				displayStyle: 'number',
			},
			{
				id: 'cat-line-break-90-2',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'cat-avg-quantity-90-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'avgQuantityLast90Days' },
					defaultValue: '0',
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
					formatOptions: { decimalPlaces: 1 },
				},
				displayStyle: 'number',
			},
		],
		headers: [
			{ id: 'h-cat-90-1', text: '90-Day Total' },
			{ id: 'h-cat-90-lb1', text: '' },
			{ id: 'h-cat-90-2', text: '90-Day Max' },
			{ id: 'h-cat-90-lb2', text: '' },
			{ id: 'h-cat-90-3', text: '90-Day Avg' },
		],
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'fixed', preset: 'xsmall' },
		verticalAlignment: 'middle',
	} as const,
];

/**
 * Client Category Insights list configuration
 * Multi-view (3 tabs): Previously Purchased, At Risk, Recommended
 * Converted from JSON to TypeScript for type safety
 */
export const CLIENT_CATEGORY_INSIGHTS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'client-category-previously-purchased',
			cacheTTL: 600000,
			name: 'Previously Purchased',
			type: 'default',
			active: true,
			columns: [
				getCategoryColumn(),
				{
					id: 'purchaseInfo',
					fields: [
						{
							id: 'last-purchase-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchasedDate' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								formatOptions: { dateFormat: 'date-short' },
							},
							displayStyle: 'date',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'frequency-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'purchaseFrequency' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h2', text: 'Last Purchase Date' },
						{ id: 'h3', text: 'Frequency' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				{
					id: 'financialMetrics',
					fields: [
						{
							id: 'total-spent-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'totalSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-financial',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'last-six-months-spent-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastSixMonthsSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-financial-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'previous-six-months-spent-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'previousSixMonthsSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h-financial-1', text: 'Total Spent' },
						{ id: 'h-financial-2', text: 'Last 6 Mo' },
						{ id: 'h-financial-3', text: 'Prev 6 Mo' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				{
					id: 'sixMonthComparison',
					fields: [
						{
							id: 'twelve-month-total-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-twelve-month',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'last-six-months-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastSixMonths' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-six-month',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'previous-six-months-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'previousSixMonths' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-twelve-month', text: '12-Mo Total' },
						{ id: 'h-six-month-1', text: 'Last 6 Mo' },
						{ id: 'h-six-month-2', text: 'Prev 6 Mo' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				...getDayWindowColumns(),
				getChartColumn(''),
			],
			searchEnabled: true,
			searchPlaceholder: 'Search categories...',
			sortOrder: 0,
		},
		{
			id: 'at-risk-categories',
			cacheTTL: 600000,
			name: 'At Risk',
			type: 'custom',
			active: true,
			filterRules: {
				viewType: { defaultValue: 'atRisk' },
			},
			columns: [
				getCategoryColumn(),
				{
					id: 'purchaseInfo',
					fields: [
						{
							id: 'last-purchase-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchasedDate' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								formatOptions: { dateFormat: 'date-short' },
							},
							displayStyle: 'date',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'days-since-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'daysSinceLastPurchase' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-3',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'frequency-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'purchaseFrequency' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h2', text: 'Last Purchase' },
						{ id: 'h3', text: 'Days Since' },
						{ id: 'h4', text: 'Frequency' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantities',
					fields: [
						{
							id: 'twelve-month-qty-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-twelve-atrisk',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'last-six-months-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastSixMonths' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-3',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'previous-six-months-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'previousSixMonths' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-twelve-mo-qty-atrisk', text: '12-Mo Qty' },
						{ id: 'h4', text: 'Last 6 Mo' },
						{ id: 'h5', text: 'Prev 6 Mo' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				{
					id: 'financialAtRisk',
					fields: [
						{
							id: 'total-spent-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'totalSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-financial-atrisk',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'last-six-months-spent-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastSixMonthsSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-financial-atrisk-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'previous-six-months-spent-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'previousSixMonthsSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h-financial-atrisk-1', text: 'Total Spent' },
						{ id: 'h-financial-atrisk-2', text: 'Last 6 Mo' },
						{ id: 'h-financial-atrisk-3', text: 'Prev 6 Mo' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				{
					id: 'avgPerOrderAtRisk',
					fields: [
						{
							id: 'distinct-items-last6-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'distinctItemsLast6Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-avg-atrisk',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'distinct-items-prev6-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'distinctItemsPrevious6Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-avg-atrisk-1', text: 'Last 6 Mo Items' },
						{ id: 'h-avg-atrisk-2', text: 'Prev 6 Mo Items' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				getChartColumn('AtRisk'),
			],
			searchEnabled: true,
			searchPlaceholder: 'Search categories...',
			sortOrder: 1,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
};
